import { z } from 'zod';

export const DAYS_OF_WEEK = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;

// Alias for backward compatibility
export const AVAILABILITY_DAYS = DAYS_OF_WEEK;

export const MENU_TYPE_VALUES = [
  'none',
  'vegetarian',
  'non_vegetarian',
  'vegan',
  'plant_based',
  'seafood',
] as const;

export const ALLOWED_ORDER_TYPES = [
  'dine_in',
  'table_order',
  'takeaway',
  'online_delivery',
  'table_booking',
] as const;

export const MENU_TYPE_LABELS: Record<(typeof MENU_TYPE_VALUES)[number], string> = {
  none: 'None',
  vegetarian: 'Vegetarian',
  non_vegetarian: 'Non-Vegetarian',
  vegan: 'Vegan',
  plant_based: 'Plant Based',
  seafood: 'Seafood',
};

export const ORDER_TYPE_LABELS: Record<(typeof ALLOWED_ORDER_TYPES)[number], string> = {
  dine_in: 'Dine In',
  table_order: 'Table Order',
  takeaway: 'Takeaway',
  online_delivery: 'Online Delivery',
  table_booking: 'Table Booking',
};

// ─── Sub-schemas ──────────────────────────────────────────────────────────────

const availabilityTimingSchema = z.object({
  from: z.string(),
  to: z.string(),
});

const availabilitySlotSchema = z.object({
  day: z.enum(DAYS_OF_WEEK),
  enabled: z.boolean(),
  timings: z.array(availabilityTimingSchema),
});

export const menuItemAttributeValueSchema = z.object({
  _idAttribute: z.string(),
  value: z.string(),
  _id: z.string().optional(),
});

// ─── Main form schema ─────────────────────────────────────────────────────────

export const menuItemFormSchema = z.object({
  images: z.array(z.string()),
  name: z.string().min(1, 'Name is required'),
  printName: z.string(),
  description: z.string(),
  _idCategory: z.string().min(1, 'Category is required'),
  priceValue: z.preprocess(
    (val) => (val === '' || val == null || (typeof val === 'number' && isNaN(val)) ? undefined : Number(val)),
    z.number({ invalid_type_error: 'Price is required', required_error: 'Price is required' }).min(0, 'Price cannot be negative')
  ),
  priceCurrency: z.string(),
  takeawayPriceValue: z.preprocess(
    (val) => (val === '' || val == null || (typeof val === 'number' && isNaN(val)) ? 0 : Number(val)),
    z.number().min(0)
  ),
  enabled: z.boolean(),
  printerId: z.string().nullable(),
  _idAllergens: z.array(z.string()),
  attributes: z.array(menuItemAttributeValueSchema),
  allergies: z.string(),
  menuType: z.enum(MENU_TYPE_VALUES),
  allowedOrderTypes: z.array(z.enum(ALLOWED_ORDER_TYPES)),
  hideMenuThumbnailImages: z.boolean(),
  calories: z.preprocess(
    (val) => (val == null ? '' : String(val)),
    z.string().regex(/^[0-9]*$/, 'Calories must be a number')
  ),
  nutritions: z.string(),
  dishnote: z.string(),
  restrictAvailability: z.boolean(),
  availabilitySlots: z.array(availabilitySlotSchema),
  _idModifiers: z.array(z.string()),
  textColor: z.string().nullable(),
  color: z.string().nullable(),
});

// ─── Inferred types ───────────────────────────────────────────────────────────

export type TMenuItemFormValues = z.infer<typeof menuItemFormSchema>;
export type TAvailabilitySlot = z.infer<typeof availabilitySlotSchema>;
export type TAvailabilityTiming = z.infer<typeof availabilityTimingSchema>;
export type TMenuItemAttributeValue = z.infer<typeof menuItemAttributeValueSchema>;
